import React, { useEffect, useMemo, useState } from "react";
import {
  X,
  Check,
  User,
  CreditCard,
  Shield,
  Car,
  MapPin,
  Camera,
} from "lucide-react";
import { toast } from "sonner";
import DriverDropdown from "../Utils/DriverDropdown";

const EditKycModal = ({
  editKycModalOpen,
  setEditKycModalOpen,
  editKycDriver,
  setEditKycDriver,
  currentStep,
  setCurrentStep,
  kycFormdata,
  setKycFormData,
  drivers,
  handleDriverSelect,
  handleFileChange,
  handleStepSubmit,
  getPincodeDetails,
  uploadImageToServer,
  resetForm,
  Loading,
  onSuccess,
  activeTab,
  FetchDriverKycDetails,
  AdminId,
}) => {
  const steps = [
    { id: 1, label: "Address", icon: MapPin },
    { id: 2, label: "Profile Picture", icon: Camera },
    { id: 3, label: "Aadhaar Card", icon: CreditCard },
    { id: 4, label: "PAN Card", icon: Shield },
    { id: 5, label: "Driving License", icon: Car },
    { id: 6, label: "Vehicle Details", icon: User },
  ];

  const [pincodeOptions, setPincodeOptions] = useState([]);
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [showPincodeOptions, setShowPincodeOptions] = useState(false);

  const getPincodeRows = (data) => {
    const possibleRows = [
      data?.response,
      data?.data,
      data?.response?.data,
      data?.response?.items,
      data?.items,
    ];

    return possibleRows.find((rows) => Array.isArray(rows)) || [];
  };

  const normalizedPincodeOptions = useMemo(
    () =>
      pincodeOptions
        .map((item) => {
          const id =
            item?.id ??
            item?.pinCodeId ??
            item?.pincodeId ??
            item?.pin_code_id ??
            item?.pk_pincode_id ??
            item?.pk_pin_code_id ??
            "";

          const pinCode =
            item?.pinCode ??
            item?.pincode ??
            item?.pin_code ??
            item?.postalCode ??
            item?.postal_code ??
            item?.code ??
            "";

          return {
            id: String(id ?? "").trim(),
            pinCode: String(pinCode ?? "").trim(),
            officeName: String(
              item?.office_name ?? item?.officeName ?? "",
            ).trim(),
            officeType: String(
              item?.office_type ?? item?.officeType ?? "",
            ).trim(),
            circle: String(item?.circle ?? "").trim(),
            region: String(item?.region ?? "").trim(),
            division: String(item?.division ?? "").trim(),
          };
        })
        .filter((item) => item.id && item.pinCode),
    [pincodeOptions],
  );

  useEffect(() => {
    const searchText = String(kycFormdata.pinCode || "").trim();

    if (
      !editKycModalOpen ||
      !searchText ||
      typeof getPincodeDetails !== "function"
    ) {
      setPincodeOptions([]);
      setShowPincodeOptions(false);
      return;
    }

    if (
      kycFormdata.pinCodeId &&
      normalizedPincodeOptions.some(
        (item) =>
          item.id === String(kycFormdata.pinCodeId) &&
          item.pinCode === searchText,
      )
    ) {
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        setPincodeLoading(true);

        const result = await getPincodeDetails({
          word: searchText,
          pageSize: 20,
          nextToken: "",
          previousToken: "",
        });

        const rows = getPincodeRows(result);

        setPincodeOptions(rows);
        setShowPincodeOptions(rows.length > 0);
      } finally {
        setPincodeLoading(false);
      }
    }, 350);

    return () => clearTimeout(timeoutId);
  }, [editKycModalOpen, getPincodeDetails, kycFormdata.pinCode]);

  const handleClose = () => {
    setEditKycModalOpen(false);
    setEditKycDriver(null);
    setCurrentStep(1);
    resetForm();
  };

  const handleNextStep = async (stepNumber, stepData) => {
    if (!kycFormdata.driver_code && stepNumber === 1) {
      toast.error("Please select a driver");
      return;
    }

    const success = await handleStepSubmit(stepNumber, stepData);
    if (success) {
      if (stepNumber < 6) {
        setCurrentStep(stepNumber + 1);
      } else {
        toast.success("All KYC steps completed successfully!");
        setTimeout(() => {
          handleClose();
          if (activeTab === "Kyc Details") {
            FetchDriverKycDetails();
          }
          if (onSuccess) onSuccess();
        }, 1500);
      }
    }
  };

  if (!editKycModalOpen) return null;

  return (
    <div>
      {editKycModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-md max-w-6xl w-full max-h-[90vh] overflow-y-auto p-6 relative mx-4">
            <div className="flex justify-between items-center mb-4 sticky top-0 bg-white pb-4 border-b">
              <h2 className="text-xl font-semibold">Edit KYC Data</h2>
              <button
                onClick={() => {
                  setEditKycModalOpen(false);
                  setEditKycDriver(null);
                  setCurrentStep(1);
                  resetForm();
                }}
                className="text-gray-600 hover:text-gray-800"
              >
                <X size={20} />
              </button>
            </div>

            <div className="bg-gradient-to-br from-orange-100 to-amber-100 p-8 rounded-2xl relative overflow-hidden">
              {/* Floating bubbles background */}
              <div className="absolute inset-0">
                {[...Array(8)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute rounded-full bg-white bg-opacity-30"
                    style={{
                      width: `${Math.random() * 25 + 15}px`,
                      height: `${Math.random() * 25 + 15}px`,
                      left: `${Math.random() * 100}%`,
                      top: `${Math.random() * 100}%`,
                      animationDelay: `${Math.random() * 3}s`,
                      animationDuration: `${4 + Math.random() * 3}s`,
                    }}
                  />
                ))}
              </div>

              {/* Additional floating elements */}
              <div className="absolute inset-0">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={`extra-${i}`}
                    className="absolute rounded-full bg-orange-200 bg-opacity-20"
                    style={{
                      width: `${Math.random() * 30 + 10}px`,
                      height: `${Math.random() * 30 + 10}px`,
                      left: `${Math.random() * 100}%`,
                      top: `${Math.random() * 100}%`,
                      animationDelay: `${Math.random() * 2}s`,
                      animationDuration: `${3 + Math.random() * 2}s`,
                    }}
                  />
                ))}
              </div>

              <div className="relative z-10">
                <div className="flex items-center justify-center space-x-8">
                  {steps.map((step, index) => {
                    const StepIcon = step.icon;
                    const isCompleted = currentStep > step.id;
                    const isCurrent = currentStep === step.id;

                    return (
                      <div key={step.id} className="flex flex-col items-center">
                        <div
                          className={`relative w-16 h-16 rounded-full transition-all duration-500 flex items-center justify-center ${
                            isCompleted
                              ? "bg-green-400 text-white shadow-lg transform scale-110"
                              : isCurrent
                                ? "bg-orange-400 text-white shadow-xl transform scale-125"
                                : "bg-white text-gray-400 shadow-md"
                          }`}
                        >
                          {/* Ripple effect for current step */}
                          {isCurrent && (
                            <>
                              <div className="absolute inset-0 rounded-full bg-orange-400 animate-ping opacity-30"></div>
                              <div className="absolute inset-0 rounded-full bg-orange-300 animate-ping opacity-20"></div>
                            </>
                          )}

                          {/* Glow effect for completed steps */}
                          {isCompleted && (
                            <div className="absolute inset-0 rounded-full bg-green-300 animate-pulse opacity-40"></div>
                          )}

                          <div className="relative z-10">
                            {isCompleted ? (
                              <Check size={24} />
                            ) : (
                              <StepIcon size={24} />
                            )}
                          </div>
                        </div>

                        <span
                          className={`mt-4 text-sm font-medium text-center ${
                            isCompleted
                              ? "text-green-600"
                              : isCurrent
                                ? "text-orange-600"
                                : "text-gray-500"
                          }`}
                        >
                          {step.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="max-w-2xl mx-auto">
              {currentStep === 1 && (
                <div className="flex justify-center">
                  <div className="w-full max-w-5xl bg-white rounded-2xl shadow-sm mt-5 p-8 border">
                    <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
                      Driver Address Information
                    </h3>
                    {!editKycDriver && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Select Driver <span className="text-red-500">*</span>
                        </label>
                        <div className="w-full pb-6">
                          <DriverDropdown
                            drivers={(drivers || []).map((driver) => ({
                              driverId: driver.driverId || driver.driver_code,
                              fullName:
                                driver.fullName ||
                                driver.name ||
                                driver.username ||
                                driver.driverId ||
                                driver.driver_code,
                            }))}
                            value={kycFormdata.driver_code}
                            onChange={(driverId) =>
                              handleDriverSelect({
                                target: {
                                  value: driverId,
                                },
                              })
                            }
                            placeholder="Choose Driver"
                            className="w-full"
                            dropdownHeight="max-h-[152px]"
                          />
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          City <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={kycFormdata.city}
                          onChange={(e) =>
                            setKycFormData((prev) => ({
                              ...prev,
                              city: e.target.value,
                            }))
                          }
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg 
                                focus:ring-2 focus:ring-orange-500 focus:border-orange-500 
                                shadow-sm"
                          placeholder="Enter City"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Area <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={kycFormdata.area}
                          onChange={(e) =>
                            setKycFormData((prev) => ({
                              ...prev,
                              area: e.target.value,
                            }))
                          }
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg 
                                  focus:ring-2 focus:ring-orange-500 focus:border-orange-500 
                                  shadow-sm"
                          placeholder="Enter Area"
                          required
                        />
                      </div>

                      <div className="relative">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Pincode <span className="text-red-500">*</span>
                        </label>

                        <input
                          type="text"
                          inputMode="numeric"
                          autoComplete="off"
                          value={kycFormdata.pinCode || ""}
                          onChange={(e) => {
                            const value = e.target.value
                              .replace(/\D/g, "")
                              .slice(0, 6);

                            setKycFormData((prev) => ({
                              ...prev,
                              pinCode: value,
                              pinCodeId: "",
                            }));

                            setShowPincodeOptions(Boolean(value));
                          }}
                          onFocus={() => {
                            if (normalizedPincodeOptions.length > 0) {
                              setShowPincodeOptions(true);
                            }
                          }}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg
                                    focus:ring-2 focus:ring-orange-500 focus:border-orange-500
                                    shadow-sm"
                          placeholder="Enter Pincode"
                          required
                        />

                        {pincodeLoading && (
                          <div className="absolute right-3 top-[42px] text-xs text-gray-400">
                            Searching...
                          </div>
                        )}

                        {showPincodeOptions &&
                          normalizedPincodeOptions.length > 0 && (
                            <div className="absolute right-0 z-50 mt-1 max-h-[190px] w-[min(300px,calc(100vw-38px))] overflow-y-auto overscroll-contain rounded-xl border border-gray-200 bg-white shadow-xl">
                              {normalizedPincodeOptions.map((item) => (
                                <button
                                  key={item.id}
                                  type="button"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => {
                                    setKycFormData((prev) => ({
                                      ...prev,
                                      pinCode: item.pinCode,
                                      pinCodeId: item.id,
                                    }));

                                    setShowPincodeOptions(false);
                                  }}
                                  className={`block w-full border-b border-gray-100 px-4 py-3 text-left transition last:border-b-0 hover:bg-orange-50 ${
                                    String(kycFormdata.pinCodeId) === item.id
                                      ? "bg-orange-50"
                                      : "bg-white"
                                  }`}
                                >
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                      <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-sm font-semibold text-gray-900">
                                          {item.pinCode}
                                        </span>

                                        {item.officeType && (
                                          <span className="rounded-md bg-orange-100 px-1.5 py-0.5 text-[10px] font-semibold text-orange-700">
                                            {item.officeType}
                                          </span>
                                        )}
                                      </div>

                                      <p className="mt-1 truncate text-xs font-medium text-gray-700">
                                        {item.officeName ||
                                          "Office name unavailable"}
                                      </p>

                                      {(item.division || item.region) && (
                                        <p className="mt-1 line-clamp-2 text-[11px] leading-4 text-gray-500">
                                          {[item.division, item.region]
                                            .filter(Boolean)
                                            .join(" • ")}
                                        </p>
                                      )}

                                      {item.circle && (
                                        <p className="mt-0.5 truncate text-[10px] text-gray-400">
                                          {item.circle}
                                        </p>
                                      )}
                                    </div>

                                    <span className="shrink-0 rounded-md bg-gray-100 px-2 py-1 text-[10px] font-medium text-gray-500">
                                      ID {item.id}
                                    </span>
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                      </div>
                    </div>

                    <div className="flex justify-end mt-10">
                      <button
                        type="button"
                        onClick={async () => {
                          if (
                            !kycFormdata.city ||
                            !kycFormdata.area ||
                            !kycFormdata.pinCode ||
                            !kycFormdata.pinCodeId ||
                            !kycFormdata.driver_code
                          ) {
                            toast.error("Please fill in all required fields");
                            return;
                          }

                          const step1Data = {
                            userid: kycFormdata.driver_code,
                            step1: {
                              city: kycFormdata.city,
                              area: kycFormdata.area,
                              pinCode: String(kycFormdata.pinCodeId),
                            },
                          };

                          const success = await handleStepSubmit(1, step1Data);
                          if (success) {
                            setCurrentStep(2);
                          }
                        }}
                        className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 
                                  rounded-lg shadow-md transition duration-200 font-medium"
                        disabled={Loading}
                      >
                        {Loading ? "Saving..." : "Next"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 2 && (
                <div className="flex justify-center">
                  <div className="w-full max-w-3xl bg-white rounded-2xl shadow-sm p-8 mt-5 border">
                    <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
                      Upload Profile Picture
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-6  w-full">
                        <label
                          htmlFor="profile_photo"
                          className="block text-sm font-medium text-gray-700 mb-3"
                        >
                          Profile Photo <span className="text-red-500">*</span>
                        </label>

                        {kycFormdata.profile_photo ? (
                          <div className="space-y-3 text-center">
                            <img
                              src={
                                typeof kycFormdata.profile_photo === "string"
                                  ? `${
                                      kycFormdata.profile_photo
                                    }?t=${Date.now()}`
                                  : URL.createObjectURL(
                                      kycFormdata.profile_photo,
                                    )
                              }
                              alt="Preview"
                              className="w-32 h-32 object-cover rounded-full mx-auto shadow-md border"
                            />
                            <div className="text-sm text-green-600 bg-green-50 px-3 py-2 rounded-lg shadow-sm">
                              {typeof kycFormdata.profile_photo === "string"
                                ? "Current Profile Photo"
                                : kycFormdata.profile_photo.name}
                            </div>
                            <div className="flex gap-2 justify-center">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) =>
                                  handleFileChange(
                                    "profile_photo",
                                    e.target.files[0],
                                  )
                                }
                                className="hidden"
                                id="change_profile_photo"
                              />
                              <label
                                htmlFor="change_profile_photo"
                                className="cursor-pointer bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 
      rounded-lg shadow-md transition duration-200 font-medium text-sm"
                              >
                                Change
                              </label>
                              <button
                                type="button"
                                onClick={() =>
                                  handleFileChange("profile_photo", null)
                                }
                                className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 
      rounded-lg shadow-md transition duration-200 font-medium text-sm"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center space-y-3">
                            <p className="text-gray-400 text-sm">
                              No file selected
                            </p>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) =>
                                handleFileChange(
                                  "profile_photo",
                                  e.target.files[0],
                                )
                              }
                              className="hidden"
                              id="profile_photo"
                            />
                            <label
                              htmlFor="profile_photo"
                              className="cursor-pointer bg-white text-orange-600 border 
                                    border-orange-600 px-4 py-2 rounded-lg 
                                    hover:bg-orange-50 text-sm shadow-sm"
                            >
                              Choose File
                            </label>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex justify-end mt-10 gap-4">
                      <button
                        type="button"
                        onClick={() => setCurrentStep(currentStep - 1)}
                        className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 
      rounded-lg shadow-md transition duration-200 font-medium"
                      >
                        Back
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          if (!kycFormdata.profile_photo) {
                            toast.error("Please upload profile photo");
                            return;
                          }

                          try {
                            let imageUrl = kycFormdata.profile_photo;

                            if (kycFormdata.profile_photo instanceof File) {
                              imageUrl = await uploadImageToServer(
                                kycFormdata.profile_photo,
                                kycFormdata.driver_code,
                                "profile_photo",
                              );
                              setKycFormData((prev) => ({
                                ...prev,
                                profile_photo: imageUrl,
                              }));
                            }

                            const step2Data = {
                              userid: kycFormdata.driver_code,
                              step2: {
                                profilePicture: imageUrl,
                              },
                            };

                            const success = await handleStepSubmit(
                              2,
                              step2Data,
                            );
                            if (success) {
                              setCurrentStep(3);
                            }
                          } catch (error) {
                            console.error("Profile upload error:", error);
                            toast.error("Failed to upload profile picture");
                          }
                        }}
                        className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 
                              rounded-lg shadow-md transition duration-200 font-medium"
                        disabled={Loading}
                      >
                        {Loading ? "Saving..." : "Next"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 3 && (
                <div className="flex justify-center mt-5">
                  <div className="w-full max-w-6xl bg-white rounded-2xl shadow-sm p-10 border">
                    <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
                      Aadhaar Information
                    </h3>

                    <div className="mb-8">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Aadhaar Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={kycFormdata.aadhaar_no}
                        onChange={(e) =>
                          setKycFormData((prev) => ({
                            ...prev,
                            aadhaar_no: e.target.value,
                          }))
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg 
            focus:ring-2 focus:ring-orange-500 focus:border-orange-500 
            shadow-sm"
                        placeholder="Enter Aadhaar number"
                        maxLength="12"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="flex flex-col items-center border-2 border-dashed border-gray-300 rounded-lg p-6 ">
                        <label
                          htmlFor="aadhaar_front"
                          className="block text-sm font-medium text-gray-700 mb-3"
                        >
                          Aadhaar Front <span className="text-red-500">*</span>
                        </label>

                        {kycFormdata.aadhaar_front ? (
                          <div className="space-y-3 text-center w-full">
                            <img
                              src={
                                typeof kycFormdata.aadhaar_front === "string"
                                  ? `${
                                      kycFormdata.aadhaar_front
                                    }?t=${Date.now()}`
                                  : URL.createObjectURL(
                                      kycFormdata.aadhaar_front,
                                    )
                              }
                              alt="Aadhaar Front"
                              className="w-full max-h-64 object-contain rounded-md mx-auto shadow-md border"
                            />
                            <div className="text-sm text-green-600 bg-green-50 px-3 py-2 rounded-lg shadow-sm">
                              {typeof kycFormdata.aadhaar_front === "string"
                                ? "Current Aadhaar Front"
                                : kycFormdata.aadhaar_front.name}
                            </div>
                            <div className="flex gap-2 justify-center">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) =>
                                  handleFileChange(
                                    "aadhaar_front",
                                    e.target.files[0],
                                  )
                                }
                                className="hidden"
                                id="change_aadhaar_front"
                              />
                              <label
                                htmlFor="change_aadhaar_front"
                                className="cursor-pointer bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 
                    rounded-lg shadow-md transition duration-200 font-medium text-sm"
                              >
                                Change
                              </label>
                              <button
                                type="button"
                                onClick={() =>
                                  handleFileChange("aadhaar_front", null)
                                }
                                className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 
                    rounded-lg shadow-md transition duration-200 font-medium text-sm"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center space-y-3">
                            <p className="text-gray-400 text-sm">
                              No file selected
                            </p>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) =>
                                handleFileChange(
                                  "aadhaar_front",
                                  e.target.files[0],
                                )
                              }
                              className="hidden"
                              id="aadhaar_front"
                            />
                            <label
                              htmlFor="aadhaar_front"
                              className="cursor-pointer bg-white text-orange-600 border 
                  border-orange-600 px-4 py-2 rounded-lg 
                  hover:bg-orange-50 text-sm shadow-sm"
                            >
                              Choose File
                            </label>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col items-center border-2 border-dashed border-gray-300 rounded-lg p-6 ">
                        <label
                          htmlFor="aadhaar_back"
                          className="block text-sm font-medium text-gray-700 mb-3"
                        >
                          Aadhaar Back <span className="text-red-500">*</span>
                        </label>

                        {kycFormdata.aadhaar_back ? (
                          <div className="space-y-3 text-center w-full">
                            <img
                              src={
                                typeof kycFormdata.aadhaar_back === "string"
                                  ? `${
                                      kycFormdata.aadhaar_back
                                    }?t=${Date.now()}`
                                  : URL.createObjectURL(
                                      kycFormdata.aadhaar_back,
                                    )
                              }
                              alt="Aadhaar Back"
                              className="w-full max-h-64 object-contain rounded-md mx-auto shadow-md border"
                            />
                            <div className="text-sm text-green-600 bg-green-50 px-3 py-2 rounded-lg shadow-sm">
                              {typeof kycFormdata.aadhaar_back === "string"
                                ? "Current Aadhaar Back"
                                : kycFormdata.aadhaar_back.name}
                            </div>
                            <div className="flex gap-2 justify-center">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) =>
                                  handleFileChange(
                                    "aadhaar_back",
                                    e.target.files[0],
                                  )
                                }
                                className="hidden"
                                id="change_aadhaar_back"
                              />
                              <label
                                htmlFor="change_aadhaar_back"
                                className="cursor-pointer bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 
                    rounded-lg shadow-md transition duration-200 font-medium text-sm"
                              >
                                Change
                              </label>
                              <button
                                type="button"
                                onClick={() =>
                                  handleFileChange("aadhaar_back", null)
                                }
                                className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 
                    rounded-lg shadow-md transition duration-200 font-medium text-sm"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center space-y-3">
                            <p className="text-gray-400 text-sm">
                              No file selected
                            </p>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) =>
                                handleFileChange(
                                  "aadhaar_back",
                                  e.target.files[0],
                                )
                              }
                              className="hidden"
                              id="aadhaar_back"
                            />
                            <label
                              htmlFor="aadhaar_back"
                              className="cursor-pointer bg-white text-orange-600 border 
                  border-orange-600 px-4 py-2 rounded-lg 
                  hover:bg-orange-50 text-sm shadow-sm"
                            >
                              Choose File
                            </label>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex justify-end mt-10 gap-4">
                      <button
                        type="button"
                        onClick={() => setCurrentStep(currentStep - 1)}
                        className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 
            rounded-lg shadow-md transition duration-200 font-medium"
                      >
                        Back
                      </button>

                      <button
                        type="button"
                        onClick={async () => {
                          const driverCode = String(
                            kycFormdata.driver_code || "",
                          ).trim();

                          const aadhaarNumber = String(
                            kycFormdata.aadhaar_no || "",
                          ).trim();

                          if (!driverCode) {
                            toast.error("Driver code is missing");
                            return;
                          }

                          if (!aadhaarNumber) {
                            toast.error("Please enter Aadhaar number");
                            return;
                          }

                          if (!kycFormdata.aadhaar_front) {
                            toast.error("Please upload Aadhaar front image");
                            return;
                          }

                          if (!kycFormdata.aadhaar_back) {
                            toast.error("Please upload Aadhaar back image");
                            return;
                          }

                          try {
                            let frontImageUrl = kycFormdata.aadhaar_front;
                            let backImageUrl = kycFormdata.aadhaar_back;

                            // Upload Aadhaar front image
                            if (kycFormdata.aadhaar_front instanceof File) {
                              const frontUploadResponse =
                                await uploadImageToServer(
                                  kycFormdata.aadhaar_front,
                                  driverCode,
                                  "aadhaar_front",
                                );

                              console.log(
                                "AADHAAR FRONT UPLOAD RESPONSE:",
                                frontUploadResponse,
                              );

                              frontImageUrl =
                                typeof frontUploadResponse === "string"
                                  ? frontUploadResponse
                                  : frontUploadResponse?.imageUrl ||
                                    frontUploadResponse?.url ||
                                    frontUploadResponse?.fileUrl ||
                                    frontUploadResponse?.file_path ||
                                    frontUploadResponse?.path ||
                                    frontUploadResponse?.data?.imageUrl ||
                                    frontUploadResponse?.data?.url ||
                                    frontUploadResponse?.data?.fileUrl ||
                                    frontUploadResponse?.data?.file_path ||
                                    frontUploadResponse?.data?.path ||
                                    frontUploadResponse?.response?.[0]
                                      ?.imageUrl ||
                                    frontUploadResponse?.response?.[0]?.url ||
                                    frontUploadResponse?.response?.[0]?.image ||
                                    frontUploadResponse?.data?.response?.[0]
                                      ?.imageUrl ||
                                    frontUploadResponse?.data?.response?.[0]
                                      ?.url ||
                                    frontUploadResponse?.data?.response?.[0]
                                      ?.image ||
                                    "";
                            }

                            // Upload Aadhaar back image
                            if (kycFormdata.aadhaar_back instanceof File) {
                              const backUploadResponse =
                                await uploadImageToServer(
                                  kycFormdata.aadhaar_back,
                                  driverCode,
                                  "aadhaar_back",
                                );

                              console.log(
                                "AADHAAR BACK UPLOAD RESPONSE:",
                                backUploadResponse,
                              );

                              backImageUrl =
                                typeof backUploadResponse === "string"
                                  ? backUploadResponse
                                  : backUploadResponse?.imageUrl ||
                                    backUploadResponse?.url ||
                                    backUploadResponse?.fileUrl ||
                                    backUploadResponse?.file_path ||
                                    backUploadResponse?.path ||
                                    backUploadResponse?.data?.imageUrl ||
                                    backUploadResponse?.data?.url ||
                                    backUploadResponse?.data?.fileUrl ||
                                    backUploadResponse?.data?.file_path ||
                                    backUploadResponse?.data?.path ||
                                    backUploadResponse?.response?.[0]
                                      ?.imageUrl ||
                                    backUploadResponse?.response?.[0]?.url ||
                                    backUploadResponse?.response?.[0]?.image ||
                                    backUploadResponse?.data?.response?.[0]
                                      ?.imageUrl ||
                                    backUploadResponse?.data?.response?.[0]
                                      ?.url ||
                                    backUploadResponse?.data?.response?.[0]
                                      ?.image ||
                                    "";
                            }

                            if (!frontImageUrl) {
                              toast.error(
                                "Aadhaar front image URL not received",
                              );
                              return;
                            }

                            if (!backImageUrl) {
                              toast.error(
                                "Aadhaar back image URL not received",
                              );
                              return;
                            }

                            // Uploaded URLs state me save karo
                            setKycFormData((prev) => ({
                              ...prev,
                              aadhaar_no: aadhaarNumber,
                              aadhaar_front: frontImageUrl,
                              aadhaar_back: backImageUrl,
                            }));

                            const step3Data = {
                              userid: driverCode,

                              step1: {
                                city: String(kycFormdata.city || "").trim(),
                                area: String(kycFormdata.area || "").trim(),
                                pinCode: String(
                                  kycFormdata.pinCodeId || "",
                                ).trim(),
                              },

                              step2: {
                                profilePicture:
                                  typeof kycFormdata.profile_photo === "string"
                                    ? kycFormdata.profile_photo
                                    : null,
                              },

                              step3: {
                                aadharNumber: aadhaarNumber,
                                aadharFrontPhoto: frontImageUrl,
                                aadharBackPhoto: backImageUrl,
                              },
                            };

                            console.log("STEP 3 FULL DATA:", step3Data);

                            const success = await handleStepSubmit(
                              3,
                              step3Data,
                            );

                            if (success) {
                              setCurrentStep(4);
                            }
                          } catch (error) {
                            console.error("Aadhaar upload error:", error);

                            toast.error(
                              error?.response?.data?.responseMessage ||
                                error?.message ||
                                "Failed to upload Aadhaar images",
                            );
                          }
                        }}
                        className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-lg shadow-md transition duration-200 font-medium disabled:cursor-not-allowed disabled:opacity-60"
                        disabled={Loading}
                      >
                        {Loading ? "Saving..." : "Next"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 4 && (
                <div className="mt-5 flex justify-center">
                  <div className="w-full max-w-4xl rounded-2xl border bg-white p-8 shadow-sm">
                    <h3 className="mb-8 text-center text-2xl font-bold text-gray-900">
                      PAN Card Information
                    </h3>

                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      {/* PAN number */}
                      <div>
                        <label className="mb-2 block text-sm font-medium text-gray-700">
                          PAN Card Number{" "}
                          <span className="text-red-500">*</span>
                        </label>

                        <input
                          type="text"
                          value={kycFormdata.pan_no || ""}
                          onChange={(event) =>
                            setKycFormData((prev) => ({
                              ...prev,
                              pan_no: event.target.value.toUpperCase(),
                            }))
                          }
                          className="w-full rounded-lg border border-gray-300 px-4 py-3 uppercase shadow-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-200"
                          placeholder="Enter PAN card number"
                          maxLength={10}
                          required
                        />
                      </div>

                      {/* PAN image */}
                      <div className="flex flex-col items-center rounded-lg border-2 border-dashed border-gray-300 p-4">
                        <label
                          htmlFor="pan_front"
                          className="mb-2 block text-sm font-medium text-gray-700"
                        >
                          PAN Card Front Image{" "}
                          <span className="text-red-500">*</span>
                        </label>

                        {kycFormdata.pan_front ? (
                          <div className="w-full space-y-3 text-center">
                            <img
                              src={
                                typeof kycFormdata.pan_front === "string"
                                  ? kycFormdata.pan_front
                                  : URL.createObjectURL(kycFormdata.pan_front)
                              }
                              alt="PAN Front"
                              className="mx-auto h-32 w-full max-w-xs rounded-md border object-contain shadow-md"
                            />

                            <div className="rounded-lg bg-green-50 px-3 py-2 text-sm text-green-600 shadow-sm">
                              {typeof kycFormdata.pan_front === "string"
                                ? "Current PAN Card"
                                : kycFormdata.pan_front.name}
                            </div>

                            <div className="flex justify-center gap-2">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(event) =>
                                  handleFileChange(
                                    "pan_front",
                                    event.target.files?.[0] || null,
                                  )
                                }
                                className="hidden"
                                id="change_pan_front"
                              />

                              <label
                                htmlFor="change_pan_front"
                                className="cursor-pointer rounded-lg bg-blue-500 px-4 py-2 text-sm font-medium text-white shadow-md transition hover:bg-blue-600"
                              >
                                Change
                              </label>

                              <button
                                type="button"
                                onClick={() =>
                                  handleFileChange("pan_front", null)
                                }
                                className="rounded-lg bg-orange-500 px-4 py-2 text-sm font-medium text-white shadow-md transition hover:bg-orange-600"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center space-y-3">
                            <p className="text-sm text-gray-400">
                              No file selected
                            </p>

                            <input
                              type="file"
                              accept="image/*"
                              onChange={(event) =>
                                handleFileChange(
                                  "pan_front",
                                  event.target.files?.[0] || null,
                                )
                              }
                              className="hidden"
                              id="pan_front"
                            />

                            <label
                              htmlFor="pan_front"
                              className="cursor-pointer rounded-lg border border-orange-600 bg-white px-4 py-2 text-sm text-orange-600 shadow-sm hover:bg-orange-50"
                            >
                              Choose File
                            </label>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="mt-10 flex justify-end gap-4">
                      <button
                        type="button"
                        onClick={() =>
                          setCurrentStep((previousStep) => previousStep - 1)
                        }
                        disabled={Loading}
                        className="rounded-lg bg-gray-500 px-8 py-3 font-medium text-white shadow-md transition hover:bg-gray-600 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        Back
                      </button>

                      <button
                        type="button"
                        disabled={Loading}
                        onClick={async () => {
                          const driverCode = String(
                            kycFormdata.driver_code || "",
                          ).trim();

                          const panNumber = String(kycFormdata.pan_no || "")
                            .trim()
                            .toUpperCase();

                          const selectedPanImage = kycFormdata.pan_front;

                          if (!driverCode) {
                            toast.error("Driver code is missing");
                            return;
                          }

                          if (!panNumber) {
                            toast.error("Please enter PAN card number");
                            return;
                          }

                          if (panNumber.length !== 10) {
                            toast.error(
                              "PAN card number must be 10 characters",
                            );
                            return;
                          }

                          if (!selectedPanImage) {
                            toast.error("Please upload PAN card image");
                            return;
                          }

                          try {
                            let panImageUrl = selectedPanImage;

                            if (selectedPanImage instanceof File) {
                              const uploadResponse = await uploadImageToServer(
                                selectedPanImage,
                                driverCode,
                                "pan_front",
                              );

                              console.log(
                                "PAN IMAGE UPLOAD RESPONSE:",
                                uploadResponse,
                              );

                              panImageUrl =
                                typeof uploadResponse === "string"
                                  ? uploadResponse
                                  : uploadResponse?.imageUrl ||
                                    uploadResponse?.url ||
                                    uploadResponse?.fileUrl ||
                                    uploadResponse?.file_path ||
                                    uploadResponse?.path ||
                                    uploadResponse?.data?.imageUrl ||
                                    uploadResponse?.data?.url ||
                                    uploadResponse?.data?.fileUrl ||
                                    uploadResponse?.data?.file_path ||
                                    uploadResponse?.data?.path ||
                                    uploadResponse?.response?.[0]?.imageUrl ||
                                    uploadResponse?.response?.[0]?.url ||
                                    uploadResponse?.response?.[0]?.image ||
                                    uploadResponse?.data?.response?.[0]
                                      ?.imageUrl ||
                                    uploadResponse?.data?.response?.[0]?.url ||
                                    uploadResponse?.data?.response?.[0]
                                      ?.image ||
                                    "";
                            }

                            if (
                              !panImageUrl ||
                              typeof panImageUrl !== "string"
                            ) {
                              toast.error("PAN card image URL not received");
                              return;
                            }

                            setKycFormData((prev) => ({
                              ...prev,
                              pan_no: panNumber,
                              pan_front: panImageUrl,
                            }));

                            const step4Data = {
                              userid: driverCode,

                              step1: {
                                city: String(kycFormdata.city || "").trim(),

                                area: String(kycFormdata.area || "").trim(),
                              },

                              step2: {
                                profilePicture:
                                  typeof kycFormdata.profile_photo === "string"
                                    ? kycFormdata.profile_photo
                                    : null,
                              },

                              step3: {
                                aadharNumber: String(
                                  kycFormdata.aadhaar_no || "",
                                ).trim(),

                                aadharFrontPhoto:
                                  typeof kycFormdata.aadhaar_front === "string"
                                    ? kycFormdata.aadhaar_front
                                    : null,

                                aadharBackPhoto:
                                  typeof kycFormdata.aadhaar_back === "string"
                                    ? kycFormdata.aadhaar_back
                                    : null,
                              },

                              step4: {
                                panCard: panNumber,
                                panCardFrontPhoto: panImageUrl,
                              },
                            };

                            console.log(
                              "STEP 4 FULL DATA:",
                              JSON.stringify(step4Data, null, 2),
                            );

                            const success = await handleStepSubmit(
                              4,
                              step4Data,
                            );

                            if (success) {
                              setCurrentStep(5);
                            }
                          } catch (error) {
                            console.error("PAN upload error:", error);

                            toast.error(
                              error?.response?.data?.responseMessage ||
                                error?.message ||
                                "Failed to upload PAN card image",
                            );
                          }
                        }}
                        className="rounded-lg bg-orange-500 px-8 py-3 font-medium text-white shadow-md transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {Loading ? "Saving..." : "Next"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 5 && (
                <div className="mt-5 flex justify-center">
                  <div className="w-full max-w-4xl rounded-2xl border bg-white p-8 shadow-sm">
                    <h3 className="mb-8 text-center text-2xl font-bold text-gray-900">
                      Driving Licence Information
                    </h3>

                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      <div>
                        <label className="mb-2 block text-sm font-medium text-gray-700">
                          Driving Licence Number{" "}
                          <span className="text-red-500">*</span>
                        </label>

                        <input
                          type="text"
                          value={kycFormdata.driving_licence || ""}
                          onChange={(event) =>
                            setKycFormData((prev) => ({
                              ...prev,
                              driving_licence: event.target.value.toUpperCase(),
                            }))
                          }
                          className="w-full rounded-lg border border-gray-300 px-4 py-3 uppercase shadow-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500"
                          placeholder="Enter Driving Licence number"
                          maxLength={16}
                          required
                        />
                      </div>

                      <div className="flex flex-col items-center rounded-lg border-2 border-dashed border-gray-300 p-4">
                        <label
                          htmlFor="driving_licence_img"
                          className="mb-2 block text-sm font-medium text-gray-700"
                        >
                          Driving Licence Image{" "}
                          <span className="text-red-500">*</span>
                        </label>

                        {kycFormdata.driving_licence_img ? (
                          <div className="w-full space-y-3 text-center">
                            <img
                              src={
                                typeof kycFormdata.driving_licence_img ===
                                "string"
                                  ? kycFormdata.driving_licence_img
                                  : URL.createObjectURL(
                                      kycFormdata.driving_licence_img,
                                    )
                              }
                              alt="Driving Licence"
                              className="mx-auto h-32 w-full max-w-xs rounded-md border object-contain shadow-md"
                            />

                            <div className="rounded-lg bg-green-50 px-3 py-2 text-sm text-green-600 shadow-sm">
                              {typeof kycFormdata.driving_licence_img ===
                              "string"
                                ? "Current Driving License"
                                : kycFormdata.driving_licence_img.name}
                            </div>

                            <div className="flex justify-center gap-2">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(event) =>
                                  handleFileChange(
                                    "driving_licence_img",
                                    event.target.files?.[0] || null,
                                  )
                                }
                                className="hidden"
                                id="change_driving_licence_img"
                              />

                              <label
                                htmlFor="change_driving_licence_img"
                                className="cursor-pointer rounded-lg bg-blue-500 px-4 py-2 text-sm font-medium text-white shadow-md transition hover:bg-blue-600"
                              >
                                Change
                              </label>

                              <button
                                type="button"
                                onClick={() =>
                                  handleFileChange("driving_licence_img", null)
                                }
                                className="rounded-lg bg-orange-500 px-4 py-2 text-sm font-medium text-white shadow-md transition hover:bg-orange-600"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center space-y-3">
                            <p className="text-sm text-gray-400">
                              No file selected
                            </p>

                            <input
                              type="file"
                              accept="image/*"
                              onChange={(event) =>
                                handleFileChange(
                                  "driving_licence_img",
                                  event.target.files?.[0] || null,
                                )
                              }
                              className="hidden"
                              id="driving_licence_img"
                            />

                            <label
                              htmlFor="driving_licence_img"
                              className="cursor-pointer rounded-lg border border-orange-600 bg-white px-4 py-2 text-sm text-orange-600 shadow-sm hover:bg-orange-50"
                            >
                              Choose File
                            </label>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="mt-10 flex justify-end gap-4">
                      <button
                        type="button"
                        onClick={() =>
                          setCurrentStep((previousStep) => previousStep - 1)
                        }
                        disabled={Loading}
                        className="rounded-lg bg-gray-500 px-8 py-3 font-medium text-white shadow-md transition hover:bg-gray-600 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        Back
                      </button>

                      <button
                        type="button"
                        disabled={Loading}
                        onClick={async () => {
                          const driverCode = String(
                            kycFormdata.driver_code || "",
                          ).trim();

                          const drivingLicenseNumber = String(
                            kycFormdata.driving_licence || "",
                          )
                            .trim()
                            .toUpperCase();

                          const selectedLicenseImage =
                            kycFormdata.driving_licence_img;

                          if (!driverCode) {
                            toast.error("Driver code is missing");
                            return;
                          }

                          if (!drivingLicenseNumber) {
                            toast.error("Please enter driving licence number");
                            return;
                          }

                          if (!selectedLicenseImage) {
                            toast.error("Please upload driving licence image");
                            return;
                          }

                          try {
                            let dlImageUrl = selectedLicenseImage;

                            if (selectedLicenseImage instanceof File) {
                              const uploadResponse = await uploadImageToServer(
                                selectedLicenseImage,
                                driverCode,
                                "driving_licence_img",
                              );

                              console.log(
                                "DRIVING LICENCE UPLOAD RESPONSE:",
                                uploadResponse,
                              );

                              dlImageUrl =
                                typeof uploadResponse === "string"
                                  ? uploadResponse
                                  : uploadResponse?.imageUrl ||
                                    uploadResponse?.url ||
                                    uploadResponse?.fileUrl ||
                                    uploadResponse?.file_path ||
                                    uploadResponse?.path ||
                                    uploadResponse?.data?.imageUrl ||
                                    uploadResponse?.data?.url ||
                                    uploadResponse?.data?.fileUrl ||
                                    uploadResponse?.data?.file_path ||
                                    uploadResponse?.data?.path ||
                                    uploadResponse?.response?.[0]?.imageUrl ||
                                    uploadResponse?.response?.[0]?.url ||
                                    uploadResponse?.response?.[0]?.image ||
                                    uploadResponse?.data?.response?.[0]
                                      ?.imageUrl ||
                                    uploadResponse?.data?.response?.[0]?.url ||
                                    uploadResponse?.data?.response?.[0]
                                      ?.image ||
                                    "";
                            }

                            if (!dlImageUrl || typeof dlImageUrl !== "string") {
                              toast.error(
                                "Driving licence image URL not received",
                              );
                              return;
                            }

                            // Uploaded URL ko state me save rakho.
                            setKycFormData((prev) => ({
                              ...prev,
                              driving_licence: drivingLicenseNumber,
                              driving_licence_img: dlImageUrl,
                            }));

                            const step5Data = {
                              userid: driverCode,

                              step1: {
                                city: String(kycFormdata.city || "").trim(),

                                area: String(kycFormdata.area || "").trim(),
                              },

                              step2: {
                                profilePicture:
                                  typeof kycFormdata.profile_photo === "string"
                                    ? kycFormdata.profile_photo
                                    : null,
                              },

                              step3: {
                                aadharNumber: String(
                                  kycFormdata.aadhaar_no || "",
                                ).trim(),

                                aadharFrontPhoto:
                                  typeof kycFormdata.aadhaar_front === "string"
                                    ? kycFormdata.aadhaar_front
                                    : null,

                                aadharBackPhoto:
                                  typeof kycFormdata.aadhaar_back === "string"
                                    ? kycFormdata.aadhaar_back
                                    : null,
                              },

                              step4: {
                                panCard: String(kycFormdata.pan_no || "")
                                  .trim()
                                  .toUpperCase(),

                                panCardFrontPhoto:
                                  typeof kycFormdata.pan_front === "string"
                                    ? kycFormdata.pan_front
                                    : null,
                              },

                              step5: {
                                drivingLicenseNumber,
                                drivingLicenseFrontPhoto: dlImageUrl,
                              },
                            };

                            console.log("STEP 5 FULL DATA:", step5Data);

                            const success = await handleStepSubmit(
                              5,
                              step5Data,
                            );

                            if (success) {
                              setCurrentStep(6);
                            }
                          } catch (error) {
                            console.error(
                              "Driving licence upload error:",
                              error,
                            );

                            toast.error(
                              error?.response?.data?.responseMessage ||
                                error?.message ||
                                "Failed to upload driving licence image",
                            );
                          }
                        }}
                        className="rounded-lg bg-orange-500 px-8 py-3 font-medium text-white shadow-md transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {Loading ? "Saving..." : "Next"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 6 && (
                <div className="mt-5">
                  <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                    <h3 className="mb-6 text-center text-lg font-semibold text-gray-800">
                      Vehicle Information
                    </h3>

                    <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                      {/* Left side fields */}
                      <div className="space-y-4">
                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-700">
                            Driving License Number
                            <span className="text-red-500"> *</span>
                          </label>

                          <input
                            type="text"
                            value={kycFormdata.driving_licence || ""}
                            onChange={(event) =>
                              setKycFormData((prev) => ({
                                ...prev,
                                driving_licence:
                                  event.target.value.toUpperCase(),
                              }))
                            }
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 uppercase outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-200"
                            placeholder="Enter Driving License Number"
                            required
                          />
                        </div>

                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-700">
                            Transport Description
                            <span className="text-red-500"> *</span>
                          </label>

                          <input
                            type="text"
                            value={kycFormdata.transport_description || ""}
                            onChange={(event) =>
                              setKycFormData((prev) => ({
                                ...prev,
                                transport_description: event.target.value,
                              }))
                            }
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-200"
                            placeholder="Example: Bike, Scooter"
                            required
                          />
                        </div>

                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-700">
                            License Plate Number
                            <span className="text-red-500"> *</span>
                          </label>

                          <input
                            type="text"
                            value={kycFormdata.licence_plate || ""}
                            onChange={(event) =>
                              setKycFormData((prev) => ({
                                ...prev,
                                licence_plate: event.target.value.toUpperCase(),
                              }))
                            }
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 uppercase outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-200"
                            placeholder="Enter License Plate Number"
                            required
                          />
                        </div>
                      </div>

                      {/* Right side image */}
                      <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 p-6">
                        <label
                          htmlFor="vehicle_name_plate_img"
                          className="mb-3 block text-sm font-medium text-gray-700"
                        >
                          License Plate Image
                          <span className="text-red-500"> *</span>
                        </label>

                        {kycFormdata.vehicle_name_plate_img ? (
                          <div className="w-full space-y-3 text-center">
                            <img
                              src={
                                typeof kycFormdata.vehicle_name_plate_img ===
                                "string"
                                  ? kycFormdata.vehicle_name_plate_img
                                  : URL.createObjectURL(
                                      kycFormdata.vehicle_name_plate_img,
                                    )
                              }
                              alt="License Plate Preview"
                              className="mx-auto h-32 w-full max-w-xs rounded-lg border object-contain shadow"
                            />

                            <div className="rounded-lg bg-green-50 px-3 py-2 text-sm text-green-600">
                              {typeof kycFormdata.vehicle_name_plate_img ===
                              "string"
                                ? "Current License Plate Image"
                                : kycFormdata.vehicle_name_plate_img.name}
                            </div>

                            <div className="flex justify-center gap-2">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(event) =>
                                  handleFileChange(
                                    "vehicle_name_plate_img",
                                    event.target.files?.[0] || null,
                                  )
                                }
                                className="hidden"
                                id="change_vehicle_name_plate_img"
                              />

                              <label
                                htmlFor="change_vehicle_name_plate_img"
                                className="cursor-pointer rounded-lg bg-blue-500 px-4 py-2 text-sm font-medium text-white shadow transition hover:bg-blue-600"
                              >
                                Change
                              </label>

                              <button
                                type="button"
                                onClick={() =>
                                  handleFileChange(
                                    "vehicle_name_plate_img",
                                    null,
                                  )
                                }
                                className="rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white shadow transition hover:bg-red-600"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-3 text-center">
                            <p className="text-sm text-gray-400">
                              No file selected
                            </p>

                            <input
                              type="file"
                              accept="image/*"
                              onChange={(event) =>
                                handleFileChange(
                                  "vehicle_name_plate_img",
                                  event.target.files?.[0] || null,
                                )
                              }
                              className="hidden"
                              id="vehicle_name_plate_img"
                            />

                            <label
                              htmlFor="vehicle_name_plate_img"
                              className="cursor-pointer rounded-lg bg-orange-500 px-5 py-2 text-sm font-medium text-white shadow transition hover:bg-orange-600"
                            >
                              Choose File
                            </label>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="mt-6 flex justify-end gap-4">
                      <button
                        type="button"
                        onClick={() =>
                          setCurrentStep((previousStep) => previousStep - 1)
                        }
                        disabled={Loading}
                        className="rounded-lg bg-gray-500 px-8 py-3 font-medium text-white shadow transition hover:bg-gray-600 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        Back
                      </button>

                      <button
                        type="button"
                        disabled={Loading}
                        onClick={async () => {
                          const driverCode = String(
                            kycFormdata.driver_code || "",
                          ).trim();

                          const drivingLicenseNumber = String(
                            kycFormdata.driving_licence || "",
                          )
                            .trim()
                            .toUpperCase();

                          const transportDescription = String(
                            kycFormdata.transport_description || "",
                          ).trim();

                          const licensePlate = String(
                            kycFormdata.licence_plate || "",
                          )
                            .trim()
                            .toUpperCase();

                          const selectedPlateImage =
                            kycFormdata.vehicle_name_plate_img;

                          if (!driverCode) {
                            toast.error("Driver code is missing");
                            return;
                          }

                          if (!drivingLicenseNumber) {
                            toast.error("Driving license number is required");
                            return;
                          }

                          if (!transportDescription) {
                            toast.error("Transport description is required");
                            return;
                          }

                          if (!licensePlate) {
                            toast.error("License plate number is required");
                            return;
                          }

                          if (!selectedPlateImage) {
                            toast.error("Please upload license plate image");
                            return;
                          }

                          try {
                            let plateImageUrl = selectedPlateImage;

                            if (selectedPlateImage instanceof File) {
                              const uploadResponse = await uploadImageToServer(
                                selectedPlateImage,
                                driverCode,
                                "vehicle_name_plate_img",
                              );

                              console.log(
                                "STEP 6 IMAGE UPLOAD RESPONSE:",
                                uploadResponse,
                              );

                              plateImageUrl =
                                typeof uploadResponse === "string"
                                  ? uploadResponse
                                  : uploadResponse?.imageUrl ||
                                    uploadResponse?.url ||
                                    uploadResponse?.fileUrl ||
                                    uploadResponse?.file_path ||
                                    uploadResponse?.path ||
                                    uploadResponse?.data?.imageUrl ||
                                    uploadResponse?.data?.url ||
                                    uploadResponse?.data?.fileUrl ||
                                    uploadResponse?.data?.file_path ||
                                    uploadResponse?.data?.path ||
                                    uploadResponse?.response?.[0]?.imageUrl ||
                                    uploadResponse?.response?.[0]?.url ||
                                    uploadResponse?.response?.[0]?.image ||
                                    uploadResponse?.data?.response?.[0]
                                      ?.imageUrl ||
                                    uploadResponse?.data?.response?.[0]?.url ||
                                    uploadResponse?.data?.response?.[0]
                                      ?.image ||
                                    "";
                            }

                            if (
                              !plateImageUrl ||
                              typeof plateImageUrl !== "string"
                            ) {
                              toast.error(
                                "Vehicle image upload failed. Image URL not received.",
                              );
                              return;
                            }

                            setKycFormData((prev) => ({
                              ...prev,
                              driving_licence: drivingLicenseNumber,
                              licence_plate: licensePlate,
                              vehicle_name_plate_img: plateImageUrl,
                            }));

                            const step6Data = {
                              userid: driverCode,

                              step1: {
                                city: String(kycFormdata.city || "").trim(),

                                area: String(kycFormdata.area || "").trim(),
                              },

                              step2: {
                                profilePicture:
                                  typeof kycFormdata.profile_photo === "string"
                                    ? kycFormdata.profile_photo
                                    : null,
                              },

                              step3: {
                                aadharNumber: String(
                                  kycFormdata.aadhaar_no || "",
                                ).trim(),

                                aadharFrontPhoto:
                                  typeof kycFormdata.aadhaar_front === "string"
                                    ? kycFormdata.aadhaar_front
                                    : null,

                                aadharBackPhoto:
                                  typeof kycFormdata.aadhaar_back === "string"
                                    ? kycFormdata.aadhaar_back
                                    : null,
                              },

                              step4: {
                                panCard: String(kycFormdata.pan_no || "")
                                  .trim()
                                  .toUpperCase(),

                                panCardFrontPhoto:
                                  typeof kycFormdata.pan_front === "string"
                                    ? kycFormdata.pan_front
                                    : null,
                              },

                              step5: {
                                drivingLicenseNumber,

                                drivingLicenseFrontPhoto:
                                  typeof kycFormdata.driving_licence_img ===
                                  "string"
                                    ? kycFormdata.driving_licence_img
                                    : null,
                              },

                              step6: {
                                transportTypeID: drivingLicenseNumber,
                                transportDescription,
                                licensePlate,
                                licensePlatePhoto: plateImageUrl,
                              },
                            };

                            console.log("STEP 6 FULL DATA:", step6Data);

                            const success = await handleStepSubmit(
                              6,
                              step6Data,
                            );

                            if (!success) {
                              return;
                            }

                            toast.success(
                              "All KYC steps completed successfully!",
                            );

                            setTimeout(() => {
                              setEditKycModalOpen(false);
                              setEditKycDriver(null);
                              setCurrentStep(1);
                              resetForm();

                              if (
                                activeTab === "Add Kyc Data" ||
                                activeTab === "Kyc Details"
                              ) {
                                FetchDriverKycDetails?.();
                              }

                              onSuccess?.();
                            }, 1500);
                          } catch (error) {
                            console.error("Vehicle plate upload error:", error);

                            toast.error(
                              error?.response?.data?.responseMessage ||
                                error?.message ||
                                "Failed to upload vehicle plate image",
                            );
                          }
                        }}
                        className="rounded-lg bg-orange-500 px-8 py-3 font-medium text-white shadow transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {Loading ? "Submitting..." : "Submit"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditKycModal;
